import { StyledTable } from 'app/components/StyledTable/StyledTable';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toFixedIfFloat } from 'utils/helpers';
import useGetTaskResourceOptions from 'utils/hooks/useGetTaskResourceOptions';
import { findResourceById } from '../../slice/helpers';
import { selectTaskUsedResourcesGroupedByCompaniesState } from '../../slice/selectors';
import { ITaskResourceGroupedByCompanyItem } from '../../slice/types';

interface IProps {
  loading: boolean;
}

const TaskCompaniesUsedResourcesTable: FC<IProps> = ({ loading }) => {
  const { t } = useTranslation();

  const resourceCompanies = useSelector(
    selectTaskUsedResourcesGroupedByCompaniesState,
  );

  const { allResources } = useGetTaskResourceOptions();

  const columns = useMemo(() => {
    const usedResources = resourceCompanies
      .map(company => company.resources)
      .flat(1);

    const withoutDuplicatesResources = usedResources.filter(
      (resource, index, self) =>
        index === self.findIndex(t => t.resourceId === resource.resourceId),
    );

    const columns = [
      {
        title: t('Common.Company', 'Company'),
        dataIndex: ['company', 'name'],
        key: 'company',
        sorter: {
          compare: (
            a: ITaskResourceGroupedByCompanyItem,
            b: ITaskResourceGroupedByCompanyItem,
          ) => {
            return a.company.name.localeCompare(b.company.name);
          },
        },
      },
    ];

    const resourceNameColumns = withoutDuplicatesResources.map(resource => ({
      title: resource.name,
      dataIndex: 'resourceTotalAmount',
      key: 'resourceTotalAmount',
      render: (
        _: unknown,
        resourceCompany: ITaskResourceGroupedByCompanyItem,
      ) => {
        const resourceFound = resourceCompany.resources.find(
          res => res.resourceId === resource.resourceId,
        );

        const globalResource = findResourceById({
          resourceId: resourceFound?.resourceId,
          allResources,
        });

        return resourceFound
          ? `${toFixedIfFloat(resourceFound.totalAmount || 0)} ${
              globalResource?.type.unit
            }`
          : '-';
      },
      sorter: {
        compare: (
          a: ITaskResourceGroupedByCompanyItem,
          b: ITaskResourceGroupedByCompanyItem,
        ) => {
          const aAmount =
            a.resources.find(res => res.resourceId === resource.resourceId)
              ?.totalAmount || 0;
          const bAmoount =
            b.resources.find(res => res.resourceId === resource.resourceId)
              ?.totalAmount || 0;
          return aAmount - bAmoount;
        },
      },
    }));

    return [...columns, ...resourceNameColumns];
  }, [t, resourceCompanies, allResources]);

  return (
    <StyledTable
      loading={loading}
      bordered={true}
      columns={columns}
      rowClassName="planned-resource-row"
      dataSource={resourceCompanies}
      pagination={false}
    />
  );
};

export default TaskCompaniesUsedResourcesTable;
